import React, {
	createContext,
	useReducer,
	useRef,
	useEffect,
	useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useQuoteState } from '../../../../../stateProviders/QuoteStateProvider';
import { getOppositeEdge, isSameEdge } from '../utils/edgeUtils';
import EdgeType from '../../../../../enums/edgeType';
import EdgeLabel from '../../../../../enums/edgeLabel';
import { Block, Edge } from '../../../../../types/shared/blocks';
import getParentBlock from '../../../../../utils/getParentBlock';
import BLOCK_GAP from '../../../../../constants/blockGap';
import getBlockLabel from '../../../../../utils/getBlockLabel';
import BlockType from '../../../../../enums/blockType';
import getBlockChildren from 'src/utils/getBlockChildren';
import CANVAS_SCALE_FACTOR from 'src/constants/scale';
import { getResponsiveDefaultPosition } from '../utils/responsiveHelpers';

interface DeckState {
	projectName: string;
	blocks: Block[];
	selectedBlock: Block | null;
	selectedEdge: { blockId: string | null; edgeLabel: EdgeLabel | null };
	displayedBlockModal: Block | null;
	scaleFactor: number;
	canvasBounds: {
		width: number;
		height: number;
		offsetX: number;
		offsetY: number;
	} | null;
	zoomScale: number;
}

export type DeckAction =
	| { type: 'ADD_BLOCK'; payload: Block }
	| { type: 'SELECT_BLOCK'; payload: Block | null }
	| {
			type: 'SELECT_EDGE';
			payload: {
				blockId: string | null | undefined;
				edgeLabel: EdgeLabel | null | undefined;
			};
	  }
	| { type: 'UPDATE_BLOCK'; payload: Block }
	| { type: 'CLEAR_SELECTION' }
	| { type: 'DELETE_BLOCK'; payload: Block }
	| { type: 'ADD_BUMPOUT'; payload: Block }
	| { type: 'ADD_STAIR'; payload: Block }
	| { type: 'ADD_LANDING'; payload: Block }
	| { type: 'INITIALIZE_STATE' }
	| { type: 'OPEN_BLOCK_MODAL'; payload: Block }
	| { type: 'CLOSE_BLOCK_MODAL' }
	| { type: 'UPDATE_BLOCKS'; payload: Block[] }
	| { type: 'SET_SCALE_FACTOR'; payload: number }
	| {
			type: 'SET_CANVAS_BOUNDS';
			payload: {
				width: number;
				height: number;
				offsetX: number;
				offsetY: number;
			};
	  }
	| { type: 'SET_ZOOM_SCALE'; payload: number }
	| {
			type: 'SET_DROPBEAM_NEEDED';
			payload: { show: boolean; block: Block; dropbeamsNeeded: number };
	  }
	| {
			type: 'SET_DROPBEAM_NOT_NEEDED';
			payload: { show: boolean; block: Block };
	  };

export type DeckContextType = {
	state: DeckState;
	dispatch: React.Dispatch<DeckAction>;
	pricingContainerRef: React.RefObject<HTMLDivElement> | null;
	transformWrapperRef: React.RefObject<any> | null;
};

const initialState: DeckState = {
	projectName: 'John test 10',
	blocks: [],
	selectedBlock: null,
	selectedEdge: { blockId: null, edgeLabel: null },
	displayedBlockModal: null,
	scaleFactor: CANVAS_SCALE_FACTOR / 12,
	canvasBounds: null,
	zoomScale: 1,
};

export const DeckContext = createContext<DeckContextType>({
	state: initialState,
	dispatch: () => null,
	pricingContainerRef: null,
	transformWrapperRef: null,
});

export const DeckBoxEdgeTypes = [
	EdgeType.NO_RAILING,
	EdgeType.RAILING,
	EdgeType.HOUSE,
	EdgeType.HOUSE_ATTACHED,
	EdgeType.HOUSE_FREESTANDING,
];

export const LandingEdgeTypes = [
	EdgeType.RAILING,
	EdgeType.NO_RAILING,
	EdgeType.BLOCK_ATTACHED,
	EdgeType.LANDING_ATTACHED,
	EdgeType.STAIR_ATTACHED,
	EdgeType.HOUSE_ATTACHED,
	EdgeType.HOUSE,
	EdgeType.GROUND,
];

export const BumpoutEdgeTypes = [
	EdgeType.NO_RAILING,
	EdgeType.DECKING,
	EdgeType.HOUSE,
];

export const StairEdgeTypes = [
	EdgeType.NO_RAILING,
	EdgeType.RAILING,
	EdgeType.BLOCK_ATTACHED,
	EdgeType.LANDING_ATTACHED,
	EdgeType.STAIR_ATTACHED,
	EdgeType.GROUND,
];

export const getEdgeColor = (type: EdgeType, isSelected: boolean) => {
	switch (type) {
		case 'House':
			return '#800000';
		case 'House Attached':
			return '#774E9F';
		case 'Railing':
			return isSelected ? '#3874CB' : '#5D9DFC';
		case 'Decking':
			return '#228B22';
		case 'No Railing':
			return 'rgba(0, 0, 0, 0)';
		case 'Ground':
			return '#9A5650';
		case 'Block Attached':
			return '#FF6600';
		case 'Landing Attached':
			return '#FF8833';
		case 'Stair Attached':
			return '#FFB066';
		case 'House Freestanding':
			return '#808080';
		default:
			return 'rgba(200, 200, 200, 0.5)';
	}
};

export const getEdgeBorderColor = (type: EdgeType) => {
	switch (type) {
		case 'No Railing':
			return '#97C15C';
		default:
			return null;
	}
};

const sanitizeBlockPositions = (blocks: Block[]): Block[] => {
	console.log('[DeckContext] Sanitizing block positions');

	const allBlocks: Block[] = [];
	blocks.forEach((block) => {
		allBlocks.push(block);
		if (block.stairs) allBlocks.push(...block.stairs);
		if (block.landings) allBlocks.push(...block.landings);
		if (block.bumpouts) allBlocks.push(...block.bumpouts);
	});

	const hasCorruptedData = allBlocks.some(
		(block) =>
			block.position &&
			(Math.abs(block.position.x) > 5000 || Math.abs(block.position.y) > 5000)
	);

	if (!hasCorruptedData) {
		console.log('[DeckContext] No corrupted positions found');
		return blocks;
	}

	console.warn('[DeckContext] Found corrupted block positions - normalizing');

	let xOffset = 0;
	let yOffset = 0;

	return blocks.map((block) => {
		const sanitizedBlock = {
			...block,
			position: { x: xOffset, y: yOffset },
		};

		if (block.stairs) {
			sanitizedBlock.stairs = block.stairs.map((stair) => ({
				...stair,
				position: stair.position
					? {
							x: Math.max(-1000, Math.min(1000, stair.position.x)),
							y: Math.max(-1000, Math.min(1000, stair.position.y)),
						}
					: { x: 0, y: 0 },
			}));
		}

		if (block.landings) {
			sanitizedBlock.landings = block.landings.map((landing) => ({
				...landing,
				position: landing.position
					? {
							x: Math.max(-1000, Math.min(1000, landing.position.x)),
							y: Math.max(-1000, Math.min(1000, landing.position.y)),
						}
					: { x: 0, y: 0 },
			}));
		}

		if (block.bumpouts) {
			sanitizedBlock.bumpouts = block.bumpouts.map((bumpout) => ({
				...bumpout,
				position: bumpout.position
					? {
							x: Math.max(-1000, Math.min(1000, bumpout.position.x)),
							y: Math.max(-1000, Math.min(1000, bumpout.position.y)),
						}
					: { x: 0, y: 0 },
			}));
		}

		xOffset += 50;
		yOffset += 50;

		console.log(
			`[DeckContext] Sanitized ${block.label || 'Block'}: ` +
				`old=(${block.position?.x}, ${block.position?.y}) ` +
				`new=(${sanitizedBlock.position.x}, ${sanitizedBlock.position.y})`
		);

		return sanitizedBlock;
	});
};

const debugReducer = (reducer) => {
	return (state, action) => {
		console.group(`Action: ${action.type}`);
		console.log(
			'%cPrevious State:',
			'color: #9E9E9E; font-weight: bold;',
			state
		);
		console.log(
			'%cAction Payload:',
			'color: #03A9F4; font-weight: bold;',
			action.payload
		);
		const nextState = reducer(state, action);
		console.log(
			'%cNext State:',
			'color: #4CAF50; font-weight: bold;',
			nextState
		);
		console.groupEnd();
		return nextState;
	};
};

const deckReducer = (state: DeckState, action) => {
	switch (action.type) {
		case 'SET_SCALE_FACTOR': {
			return {
				...state,
				scaleFactor: action.payload,
			};
		}

		case 'SET_CANVAS_BOUNDS': {
			return {
				...state,
				canvasBounds: action.payload,
			};
		}

		case 'SET_ZOOM_SCALE': {
			console.log('[DeckContext] SET_ZOOM_SCALE:', action.payload);
			return {
				...state,
				zoomScale: action.payload,
			};
		}

		case 'ADD_BLOCK':
			if (action.payload.type === 'DeckBox') {
				const isFirstBlock = state.blocks.length === 0;

				let defaultPosition;

				if (isFirstBlock) {
					defaultPosition = getResponsiveDefaultPosition();
				} else {
					const lastBlock = state.blocks[state.blocks.length - 1];
					const offset = 50;

					defaultPosition = {
						x: (lastBlock.position?.x || 0) + offset,
						y: (lastBlock.position?.y || 0) + offset,
					};
				}

				const finalPosition = action.payload.position || defaultPosition;

				const blockData = {
					...action.payload,
					id: uuidv4(),
					position: finalPosition,
					rotation: 0,
					bumpouts: [],
					stairs: [],
					landings: [],
					label: getBlockLabel(state.blocks, action.payload),
				};

				return {
					...state,
					blocks: [...state.blocks, blockData],
					selectedBlock: blockData,
				};
			} else {
				const blockData = {
					...action.payload,
					id: uuidv4(),
					position: action.payload.position || { x: 0, y: 0 },
				};

				return {
					...state,
					blocks: [...state.blocks, blockData],
					selectedBlock: blockData,
				};
			}

		case 'SELECT_BLOCK': {
			return {
				...state,
				selectedBlock: action.payload,
				selectedEdge: null,
			};
		}

		case 'SELECT_EDGE':
			return {
				...state,
				selectedEdge: {
					blockId: action.payload.blockId,
					edgeLabel: action.payload.edgeLabel,
				},
				displayedBlockModal: null,
			};

		case 'OPEN_BLOCK_MODAL': {
			return {
				...state,
				displayedBlockModal: action.payload,
			};
		}

		case 'CLOSE_BLOCK_MODAL':
			return {
				...state,
				displayedBlockModal: null,
			};

		case 'UPDATE_BLOCK': {
			const updatedBlocks = state.blocks.map((blk) => {
				if (blk.id === action.payload.id) {
					const updatedBlock = {
						...blk,
						...action.payload,
					};

					if (
						updatedBlock.type === BlockType.DECKBOX &&
						(updatedBlock.bumpouts?.length > 0 ||
							updatedBlock.stairs?.length > 0 ||
							updatedBlock.landings?.length > 0)
					) {
						const updatedBumpouts = updatedBlock.bumpouts?.map((bump) => {
							const bumpEdge = bump.edges.find(
								(edge) => edge.type === EdgeType.BLOCK_ATTACHED
							);

							let newPosition = bump.position;

							switch (bumpEdge?.label) {
								case EdgeLabel.TOP:
									newPosition = {
										x: updatedBlock.position?.x || 0,
										y: updatedBlock.position?.y || 0,
									};
									break;
								case EdgeLabel.BOTTOM:
									newPosition = {
										x: updatedBlock.position?.x || 0,
										y:
											(updatedBlock.position?.y || 0) +
											updatedBlock.displayDimensions.height *
												state.scaleFactor -
											bump.displayDimensions.height * state.scaleFactor,
									};
									break;
								case EdgeLabel.LEFT:
									newPosition = {
										x: updatedBlock.position?.x || 0,
										y: bump.position?.y || 0,
									};
									break;
								case EdgeLabel.RIGHT:
									newPosition = {
										x:
											(updatedBlock.position?.x || 0) +
											updatedBlock.displayDimensions.width * state.scaleFactor -
											bump.displayDimensions.width * state.scaleFactor,
										y: bump.position?.y || 0,
									};
									break;
							}

							return {
								...bump,
								position: newPosition,
							};
						});

						const updatedLandings = updatedBlock.landings?.map((landing) => {
							const landingEdge = landing.edges.find(
								(edge) => edge.type === EdgeType.BLOCK_ATTACHED
							);

							let newPosition = landing.position;

							switch (landingEdge?.label) {
								case EdgeLabel.TOP:
									newPosition = {
										x: landing.position?.x || 0,
										y:
											(updatedBlock.position?.y || 0) +
											updatedBlock.displayDimensions.height *
												state.scaleFactor +
											BLOCK_GAP,
									};
									break;
								case EdgeLabel.LEFT:
									newPosition = {
										x:
											(updatedBlock.position?.x || 0) +
											updatedBlock.displayDimensions.width * state.scaleFactor +
											BLOCK_GAP,
										y: landing.position?.y || 0,
									};
									break;
								case EdgeLabel.BOTTOM:
									newPosition = {
										x: landing.position?.x || 0,
										y:
											(updatedBlock.position?.y || 0) -
											landing.displayDimensions.height * state.scaleFactor -
											BLOCK_GAP,
									};
									break;
								case EdgeLabel.RIGHT:
									newPosition = {
										x:
											(updatedBlock.position?.x || 0) -
											landing.displayDimensions.width * state.scaleFactor -
											BLOCK_GAP,
										y: landing.position?.y || 0,
									};
									break;
							}

							return {
								...landing,
								position: newPosition,
							};
						});

						const updatedStairs = updatedBlock.stairs?.map((stair) => {
							const stairEdge = stair.edges.find(
								(edge) => edge.type === EdgeType.BLOCK_ATTACHED
							);

							const stairParentLanding = updatedLandings?.find(
								(landing) => landing.id === stair.parentBlock?.id
							);

							const parentForStair = stairParentLanding || updatedBlock;

							let newPosition = stair.position;

							switch (stairEdge?.label) {
								case EdgeLabel.TOP:
									newPosition = {
										x: stair.position?.x || 0,
										y:
											(parentForStair.position?.y || 0) +
											parentForStair.displayDimensions.height *
												state.scaleFactor +
											BLOCK_GAP,
									};
									break;
								case EdgeLabel.LEFT:
									newPosition = {
										x:
											(parentForStair.position?.x || 0) +
											parentForStair.displayDimensions.width *
												state.scaleFactor +
											BLOCK_GAP,
										y: stair.position?.y || 0,
									};
									break;
								case EdgeLabel.BOTTOM:
									newPosition = {
										x: stair.position?.x || 0,
										y:
											(parentForStair.position?.y || 0) -
											stair.displayDimensions.height * state.scaleFactor -
											BLOCK_GAP,
									};
									break;
								case EdgeLabel.RIGHT:
									newPosition = {
										x:
											(parentForStair.position?.x || 0) -
											stair.displayDimensions.width * state.scaleFactor -
											BLOCK_GAP,
										y: stair.position?.y || 0,
									};
									break;
							}

							return {
								...stair,
								position: newPosition,
							};
						});

						return {
							...updatedBlock,
							bumpouts: updatedBumpouts,
							stairs: updatedStairs,
							landings: updatedLandings,
						};
					}

					return updatedBlock;
				} else if (blk.type === 'DeckBox') {
					const updatedBumpouts = blk.bumpouts?.map((bump) => {
						if (bump.id !== action.payload.id) return bump;

						const { parentBlock } = getParentBlock(
							state.blocks,
							bump.parentBlock
						);

						if (!parentBlock) {
							console.warn(`Block with id ${bump.parentBlock.id} not found.`);
							return state;
						}

						return { ...bump, ...action.payload };
					});

					const updatedStairs = blk.stairs?.map((stair) => {
						if (stair.id !== action.payload.id) return stair;

						const { parentBlock } = getParentBlock(
							state.blocks,
							stair.parentBlock
						);

						if (!parentBlock) {
							console.warn(`Block with id ${stair.parentBlock.id} not found.`);
							return state;
						}

						const blockEdge = stair.edges.find(
							(edge) => edge.type === EdgeType.BLOCK_ATTACHED
						);

						const newDeckBoxEdge = action.payload.edges.find(
							(edge) => edge.type === EdgeType.BLOCK_ATTACHED
						);

						if (!newDeckBoxEdge) {
							console.warn('No BLOCK_ATTACHED edge found in stair update');
							return { ...stair, ...action.payload };
						}

						switch (newDeckBoxEdge.label) {
							case 'Top':
								action.payload.position = {
									x:
										newDeckBoxEdge &&
										blockEdge &&
										isSameEdge(newDeckBoxEdge, blockEdge)
											? action.payload.position.x
											: 0,
									y:
										(parentBlock.position?.y || 0) +
										parentBlock.displayDimensions.height * state.scaleFactor +
										BLOCK_GAP,
								};
								break;
							case 'Left':
								action.payload.position = {
									x:
										(parentBlock.position?.x || 0) +
										parentBlock.displayDimensions.width * state.scaleFactor +
										BLOCK_GAP,
									y:
										newDeckBoxEdge &&
										blockEdge &&
										isSameEdge(newDeckBoxEdge, blockEdge)
											? action.payload.position.y
											: 0,
								};
								break;
							case 'Bottom':
								action.payload.position = {
									x:
										newDeckBoxEdge &&
										blockEdge &&
										isSameEdge(newDeckBoxEdge, blockEdge)
											? action.payload.position.x
											: 0,
									y:
										(parentBlock.position?.y || 0) -
										stair.displayDimensions.height * state.scaleFactor -
										BLOCK_GAP,
								};
								break;
							case 'Right':
								action.payload.position = {
									x:
										(parentBlock.position?.x || 0) -
										stair.displayDimensions.width * state.scaleFactor -
										BLOCK_GAP,
									y:
										newDeckBoxEdge &&
										blockEdge &&
										isSameEdge(newDeckBoxEdge, blockEdge)
											? action.payload.position.y
											: 0,
								};
								break;
						}

						return { ...stair, ...action.payload };
					});

					const updatedLandings = blk.landings?.map((landing) => {
						if (landing.id !== action.payload.id) return landing;

						const { parentBlock } = getParentBlock(
							state.blocks,
							landing.parentBlock
						);

						if (!parentBlock) {
							console.warn(
								`Block with id ${landing.parentBlock.id} not found.`
							);
							return state;
						}

						const blockEdge = landing.edges.find(
							(edge) => edge.type === EdgeType.BLOCK_ATTACHED
						);

						const newDeckBoxEdge = action.payload.edges.find(
							(edge) => edge.type === EdgeType.BLOCK_ATTACHED
						);

						// Only recalculate position if the edge has changed
						if (!isSameEdge(newDeckBoxEdge, blockEdge)) {
							switch (newDeckBoxEdge.label) {
								case 'Top':
									action.payload.position = {
										x: 0,
										y:
											(parentBlock.position?.y || 0) +
											parentBlock.displayDimensions.height * state.scaleFactor +
											BLOCK_GAP,
									};
									break;
								case 'Left':
									action.payload.position = {
										x:
											(parentBlock.position?.x || 0) +
											parentBlock.displayDimensions.width * state.scaleFactor +
											BLOCK_GAP,
										y: 0,
									};
									break;
								case 'Bottom':
									action.payload.position = {
										x: 0,
										y:
											(parentBlock.position?.y || 0) -
											landing.displayDimensions.height * state.scaleFactor -
											BLOCK_GAP,
									};
									break;
								case 'Right':
									action.payload.position = {
										x:
											(parentBlock.position?.x || 0) -
											landing.displayDimensions.width * state.scaleFactor -
											BLOCK_GAP,
										y: 0,
									};
									break;
							}
						}

						return { ...landing, ...action.payload };
					});

					return {
						...blk,
						bumpouts: updatedBumpouts,
						stairs: updatedStairs,
						landings: updatedLandings,
					};
				} else {
					return blk;
				}
			});

			return {
				...state,
				blocks: updatedBlocks,
				selectedBlock:
					state.selectedBlock && state.selectedBlock.id === action.payload.id
						? { ...state.selectedBlock, ...action.payload }
						: state.selectedBlock,
			};
		}

		case 'UPDATE_BLOCKS': {
			return {
				...state,
				blocks: action.payload,
			};
		}

		case 'CLEAR_SELECTION':
			return {
				...state,
				selectedBlock: null,
				selectedEdge: null,
			};

		case 'DELETE_BLOCK': {
			const allChildren = getBlockChildren(state.blocks, action.payload);

			const blocksToDeleteIds = [action.payload, ...allChildren].map(
				(blk) => blk.id
			);

			return {
				...state,
				blocks: state.blocks
					.filter((blk) => !blocksToDeleteIds.includes(blk.id))
					.map((blk) => {
						if (blk.type === 'DeckBox') {
							return {
								...blk,
								bumpouts: blk.bumpouts.filter(
									(b) => !blocksToDeleteIds.includes(b.id)
								),
								landings: blk.landings?.filter(
									(l) => !blocksToDeleteIds.includes(l.id)
								),
								stairs: blk.stairs?.filter(
									(s) => !blocksToDeleteIds.includes(s.id)
								),
							};
						}

						return blk;
					}),
				selectedBlock: null,
				selectedEdge: null,
				displayedBlockModal: null,
			};
		}
		case 'ADD_BUMPOUT': {
			const bumpout = action.payload;

			const { parentDeckBoxId, parentBlock } = getParentBlock(
				state.blocks,
				bumpout.parentBlock
			);

			if (!parentBlock) {
				console.warn(`Block with id ${bumpout.parentBlock.id} not found.`);
				return state;
			}

			// Set initial position (e.g., top-left corner inside DeckBox)
			const initialPosition = {
				x: parentBlock.position?.x || 0, // Start at the left edge
				y: parentBlock.position?.y || 0, // Start at the top edge
			};

			const newBumpout = {
				...bumpout,
				id: uuidv4(),
				position: initialPosition,
				rotation: 0, // Initialize rotation
			};

			return {
				...state,
				blocks: state.blocks.map((blk) => {
					if (blk.id === parentDeckBoxId && blk.type === 'DeckBox') {
						return {
							...blk,
							bumpouts: [
								...blk.bumpouts,
								{
									...newBumpout,
									label: getBlockLabel(state.blocks, newBumpout),
								},
							],
						};
					}
					return blk;
				}),
				selectedBlock: newBumpout,
			};
		}
		case 'ADD_STAIR': {
			const stair = action.payload;

			const { parentDeckBoxId, parentBlock } = getParentBlock(
				state.blocks,
				stair.parentBlock
			);

			if (!parentBlock) {
				console.warn(`Block with id ${stair.parentBlock.id} not found.`);
				return state;
			}

			let initialPosition;

			const blockEdge = stair.edges.find(
				(edge) => edge.type === EdgeType.BLOCK_ATTACHED
			);
			switch (blockEdge.label) {
				case 'Top':
					initialPosition = {
						x: parentBlock.position?.x || 0,
						y:
							(parentBlock.position?.y || 0) +
							parentBlock.displayDimensions.height * state.scaleFactor +
							BLOCK_GAP,
					};
					break;
				case 'Bottom':
					initialPosition = {
						x: parentBlock.position?.x || 0,
						y:
							(parentBlock.position?.y || 0) -
							stair.displayDimensions.height * state.scaleFactor -
							BLOCK_GAP,
					};
					break;
				case 'Left':
					initialPosition = {
						x:
							(parentBlock.position?.x || 0) +
							parentBlock.displayDimensions.width * state.scaleFactor +
							BLOCK_GAP,
						y: parentBlock.position?.y || 0,
					};
					break;
				case 'Right':
					initialPosition = {
						x:
							(parentBlock.position?.x || 0) -
							stair.displayDimensions.width * state.scaleFactor -
							BLOCK_GAP,
						y: parentBlock.position?.y || 0,
					};
					break;
			}

			const newStair = {
				...stair,
				id: stair.id || uuidv4(),
				position: initialPosition,
				rotation: 0,
			};

			return {
				...state,
				blocks: state.blocks.map((blk) => {
					if (blk.type === 'DeckBox') {
						const newBlock = { ...blk };

						if (blk.id === parentDeckBoxId) {
							newBlock.stairs = [
								...blk.stairs,
								{
									...newStair,
									label: getBlockLabel(state.blocks, newStair),
								},
							];
						}

						if (parentBlock.type == BlockType.LANDING) {
							const edgeAttachedTo = getOppositeEdge(blockEdge.label);

							if (blk.landings)
								newBlock.landings = blk.landings.map((landing) =>
									landing.id == parentBlock.id
										? {
												...landing,
												edges: landing.edges.map((edge) => {
													if (
														edge.label !== edgeAttachedTo ||
														edge.type === EdgeType.BLOCK_ATTACHED
													) {
														return edge;
													}
													return {
														...edge,
														type: EdgeType.STAIR_ATTACHED,
													};
												}),
											}
										: landing
								);
						}

						return newBlock;
					}

					return blk;
				}),
				selectedBlock: newStair,
			};
		}
		case 'ADD_LANDING': {
			const landing = action.payload;

			const { parentDeckBoxId, parentBlock } = getParentBlock(
				state.blocks,
				landing.parentBlock
			);

			if (!parentBlock) {
				console.warn(`Block with id ${landing.parentBlock.id} not found.`);
				return state;
			}

			let initialPosition;

			const blockEdge: Edge = landing.edges.find(
				(edge) => edge.type === EdgeType.BLOCK_ATTACHED
			);
			switch (blockEdge.label) {
				case 'Top':
					initialPosition = {
						x: parentBlock.position?.x || 0,
						y:
							(parentBlock.position?.y || 0) +
							parentBlock.displayDimensions.height * state.scaleFactor +
							BLOCK_GAP,
					};
					break;
				case 'Bottom':
					initialPosition = {
						x: parentBlock.position?.x || 0,
						y:
							(parentBlock.position?.y || 0) -
							landing.displayDimensions.height * state.scaleFactor -
							BLOCK_GAP,
					};
					break;
				case 'Left':
					initialPosition = {
						x:
							(parentBlock.position?.x || 0) +
							parentBlock.displayDimensions.width * state.scaleFactor +
							BLOCK_GAP,
						y: parentBlock.position?.y || 0,
					};
					break;
				case 'Right':
					initialPosition = {
						x:
							(parentBlock.position?.x || 0) -
							landing.displayDimensions.width * state.scaleFactor -
							BLOCK_GAP,
						y: parentBlock.position?.y || 0,
					};
					break;
			}

			const newLanding = {
				...landing,
				id: landing.id || uuidv4(),
				position: initialPosition,
				rotation: 0,
			};

			return {
				...state,
				blocks: state.blocks.map((blk) => {
					if (blk.type === 'DeckBox') {
						const newBlock = { ...blk };

						if (blk.id === parentDeckBoxId) {
							newBlock.landings = [
								...blk.landings,
								{
									...newLanding,
									label: getBlockLabel(state.blocks, newLanding),
								},
							];
						}

						if (parentBlock.type == BlockType.STAIR) {
							const edgeAttachedTo = getOppositeEdge(blockEdge.label);

							if (blk.stairs)
								newBlock.stairs = blk.stairs.map((stair) =>
									stair.id == parentBlock.id
										? {
												...stair,
												edges: stair.edges.map((edge) => {
													if (
														edge.label !== edgeAttachedTo ||
														edge.type === EdgeType.BLOCK_ATTACHED
													) {
														return edge;
													}
													return {
														...edge,
														type: EdgeType.LANDING_ATTACHED,
													};
												}),
											}
										: stair
								);
						}

						return newBlock;
					}

					return blk;
				}),
				selectedBlock: newLanding,
			};
		}
		case 'SET_DROPBEAM_NEEDED': {
			const { show, block, dropbeamsNeeded } = action.payload;

			return {
				...state,
				dropbeamNeeded: {
					show,
					block,
					dropbeamsNeeded,
				},
			};
		}
		case 'SET_DROPBEAM_NOT_NEEDED': {
			const { show, block } = action.payload;

			return {
				...state,
				dropbeamNotNeeded: {
					show,
					block,
				},
			};
		}

		case 'INITIALIZE_STATE': {
			console.log('[DeckContext] INITIALIZE_STATE called');
			const sanitizedBlocks = sanitizeBlockPositions(state.blocks);
			return {
				...state,
				blocks: sanitizedBlocks,
			};
		}

		default:
			console.warn(`Unhandled action type: ${action.type}`);
			return state;
	}
};

export const DeckProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const qouteState = useQuoteState();

	initialState.blocks = qouteState.blocks.length > 0 ? qouteState.blocks : [];

	const [state, dispatch] = useReducer(debugReducer(deckReducer), initialState);

	const pricingContainerRef = useRef<HTMLDivElement>(null);
	const transformWrapperRef = useRef<any>(null);

	useEffect(() => {
		dispatch({ type: 'INITIALIZE_STATE' });
	}, []);

	return (
		<DeckContext.Provider
			value={{ state, dispatch, pricingContainerRef, transformWrapperRef }}
		>
			{children}
		</DeckContext.Provider>
	);
};
